import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { siteConfigService, socialMediaService } from '../services/api';
import { Facebook, Instagram, Linkedin, Youtube, Mail, Phone, MapPin, ArrowRight, Sparkles } from 'lucide-react';
import XLogo from './XLogo';

function Footer() {
  const [footerLinks, setFooterLinks] = useState({});
  const [settings, setSettings] = useState({});
  const [socialLinks, setSocialLinks] = useState({});

  useEffect(() => {
    loadFooter();
  }, []);

  const loadFooter = async () => {
    try {
      const [links, settingsData, socialRes] = await Promise.all([
        siteConfigService.footer().catch(() => null),
        siteConfigService.settings().catch(() => ({})),
        socialMediaService.index().catch(() => null),
      ]);
      if (links) setFooterLinks(groupFooterLinks(links));
      setSettings(settingsData?.data || settingsData || {});
      if (socialRes) setSocialLinks(socialRes.data || {});
    } catch (e) {
      console.error('Footer load error:', e);
    }
  };

  const groupFooterLinks = (linksResponse) => {
    if (!linksResponse) return {};
    const list = Array.isArray(linksResponse?.data) ? linksResponse.data : Array.isArray(linksResponse) ? linksResponse : [];
    return list.reduce((acc, link) => {
      if (link.is_active === false) return acc;
      const column = link.column_title || 'Links';
      if (!acc[column]) {
        acc[column] = {
          items: [],
          index: typeof link.column_index === 'number' ? link.column_index : Number.MAX_SAFE_INTEGER,
        };
      }
      acc[column].items.push(link);
      acc[column].items.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
      return acc;
    }, {});
  };

  const columns = Object.entries(footerLinks || {}).sort((a, b) => (a[1].index ?? 0) - (b[1].index ?? 0));

  const socialIcons = [
    { key: 'social_facebook', icon: Facebook, label: 'Facebook' },
    { key: 'social_twitter', icon: XLogo, label: 'X (Twitter)' },
    { key: 'social_instagram', icon: Instagram, label: 'Instagram' },
    { key: 'social_linkedin', icon: Linkedin, label: 'LinkedIn' },
    { key: 'social_youtube', icon: Youtube, label: 'YouTube' },
  ];

  const siteName = settings?.site_name || 'Ortus';
  const siteLogo = settings?.site_logo;
  const tagline = settings?.site_tagline || 'FMCG Limited';

  return (
    <footer className="bg-slate-950 text-slate-300 relative overflow-hidden">
      {/* Decorative gradient orbs */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-red-600 opacity-5 blur-3xl rounded-full"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-red-600 opacity-5 blur-3xl rounded-full"></div>

      {/* Top accent bar */}
      <div className="h-1 bg-gradient-to-r from-red-600 via-red-500 to-red-700"></div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        {/* Main footer content */}
        <div className="py-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            {/* Brand column - spans 4 */}
            <div className="md:col-span-4">
              <Link to="/" className="flex items-center gap-3 mb-5 group">
                {siteLogo ? (
                  <img src={siteLogo} alt={siteName} className="h-14 w-auto max-w-[160px] object-contain" />
                ) : (
                  <>
                    <span className="h-11 w-11 rounded-xl bg-red-600 text-white flex items-center justify-center font-black text-xl shadow-lg group-hover:bg-red-700 transition">
                      {siteName.charAt(0)}
                    </span>
                    <span>
                      <span className="block text-2xl font-black text-white tracking-tight leading-none">{siteName}</span>
                      <span className="block text-[11px] uppercase tracking-[0.28em] text-red-500 font-semibold mt-1">{tagline}</span>
                    </span>
                  </>
                )}
              </Link>
              <p className="text-sm leading-relaxed text-slate-400 max-w-sm mb-6">
                {settings?.meta_description || 'A food-led FMCG company delivering quality products across home care, personal care, and beauty categories.'}
              </p>

              {/* Contact info */}
              <div className="space-y-3">
                {settings?.contact_phone && (
                  <a href={`tel:${settings.contact_phone}`} className="flex items-center gap-3 text-sm text-slate-300 hover:text-red-400 transition group">
                    <span className="w-9 h-9 rounded-lg bg-slate-900 flex items-center justify-center group-hover:bg-red-600/20 transition">
                      <Phone size={15} className="text-red-500" />
                    </span>
                    {settings.contact_phone}
                  </a>
                )}
                {settings?.contact_email && (
                  <a href={`mailto:${settings.contact_email}`} className="flex items-center gap-3 text-sm text-slate-300 hover:text-red-400 transition group">
                    <span className="w-9 h-9 rounded-lg bg-slate-900 flex items-center justify-center group-hover:bg-red-600/20 transition">
                      <Mail size={15} className="text-red-500" />
                    </span>
                    {settings.contact_email}
                  </a>
                )}
                {settings?.contact_address && (
                  <div className="flex items-center gap-3 text-sm text-slate-300">
                    <span className="w-9 h-9 rounded-lg bg-slate-900 flex items-center justify-center flex-shrink-0">
                      <MapPin size={15} className="text-red-500" />
                    </span>
                    <span className="whitespace-pre-wrap">{settings.contact_address}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Link columns - spans 8 */}
            <div className="md:col-span-8">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                {columns.map(([columnTitle, columnMeta]) => (
                  <div key={columnTitle}>
                    <h3 className="text-white font-bold text-sm uppercase tracking-wider mb-5 flex items-center gap-2">
                      <span className="w-1 h-4 bg-red-600 rounded-full"></span>
                      {columnTitle}
                    </h3>
                    <ul className="space-y-3">
                      {columnMeta.items.map((link) => (
                        <li key={link.id}>
                          <Link
                            to={link.url}
                            className="text-sm text-slate-400 hover:text-white transition-all duration-200 hover:translate-x-1 inline-block"
                          >
                            {link.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Newsletter strip */}
        <div className="border-t border-slate-800/50 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <Sparkles size={20} className="text-red-500" />
              <div>
                <p className="text-white font-bold text-sm">Stay Connected</p>
                <p className="text-slate-400 text-xs mt-0.5">Get the latest updates and offers from Ortus FMCG</p>
              </div>
            </div>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 bg-red-600 text-white px-6 py-3 rounded-full text-sm font-bold hover:bg-red-700 transition-all shadow-lg shadow-red-900/30 hover:shadow-red-900/50 hover:scale-105"
            >
              Read Our Blog <ArrowRight size={16} />
            </Link>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-slate-800/50 py-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex flex-col md:flex-row items-center gap-4 md:gap-6">
            <p className="text-slate-400 text-xs">
              &copy; {new Date().getFullYear()} {settings?.copyright_text || 'Ortus FMCG Limited'}. All Rights Reserved.
            </p>
            <p className="text-slate-400 text-xs">
              Design and developed by{' '}
              <a href="https://yashoraj.com" target="_blank" rel="noopener noreferrer" className="text-slate-300 hover:text-red-400 transition">
                Yashoraj IT Solutions
              </a>
            </p>
          </div>

          <div className="flex items-center gap-4">
            {/* Social icons */}
            <div className="flex gap-2">
              {socialIcons.map((social) => {
                const url = socialLinks[social.key.replace('social_', '')] || settings[social.key];
                if (!url) return null;
                const Icon = social.icon;
                return (
                  <a
                    key={social.key}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-lg bg-slate-900 flex items-center justify-center text-slate-300 hover:bg-red-600 hover:text-white transition-all duration-300 hover:scale-110"
                    title={social.label}
                  >
                    <Icon size={16} strokeWidth={2} />
                  </a>
                );
              })}
            </div>

            {/* Legal links */}
            <div className="flex gap-4 pl-4 border-l border-slate-800">
              <Link to="/privacy" className="text-slate-400 hover:text-red-400 transition text-xs">Privacy</Link>
              <Link to="/terms" className="text-slate-400 hover:text-red-400 transition text-xs">Terms</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
